import type { UpdateCustomerPayload } from '@/services/customer.service';
import type { UserType } from '@/pages/POS/types';
import { serializeEmailList } from '@/utils/emailList';
import { toIsoBirthday } from './buildCreateOrderPayload';

/**
 * Giá trị form POS mà dialog "Sửa nhanh thông tin khách hàng" ghi vào.
 * Chỉ liệt kê field dialog đó thật sự sửa được — cố tình KHÔNG nhận cả
 * `form.getFieldsValue()` để không âm thầm đẩy field lạ lên hồ sơ khách.
 */
export interface CustomerProfileFormValues {
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_address?: string;
  customer_city_id?: string;
  customer_ward_id?: string;
  customer_birthday?: string;
  citizen_id?: string;
  description?: string;
  invoice_name?: string;
  invoice_tax_id?: string;
  invoice_address?: string;
  invoice_email?: string;
  buyer_name?: string;
  buyer_cccd?: string;
}

function trimmed(v: string | undefined): string | undefined {
  const s = v?.trim();
  return s ? s : undefined;
}

/**
 * TCR-1146: dựng payload `PUT /customers/:id` từ những gì thu ngân vừa gõ trong
 * dialog sửa nhanh, để hồ sơ khách được cập nhật NGAY tại màn tạo đơn thay vì
 * chờ tới lúc thanh toán.
 *
 * Field trống bị BỎ HẲN khỏi payload (không gửi `''`): BE `buildTenantCustomerData`
 * ghi đè bằng đúng giá trị nhận được, nên gửi chuỗi rỗng cho ô thu ngân không
 * đụng tới sẽ XOÁ dữ liệu đang có trên hồ sơ khách — mất CCCD/MST mà không có
 * gì trên màn hình báo.
 */
export function buildCustomerProfileUpdate(
  values: CustomerProfileFormValues,
  userType: UserType,
): UpdateCustomerPayload {
  const payload: UpdateCustomerPayload = {};

  const name = trimmed(values.customer_name);
  if (name) payload.name = name;
  const phone = trimmed(values.customer_phone);
  if (phone) payload.phone = phone;
  const email = trimmed(values.customer_email);
  if (email) payload.email = email;
  const address = trimmed(values.customer_address);
  if (address) payload.address = address;
  if (values.customer_city_id) payload.cityId = values.customer_city_id;
  if (values.customer_ward_id) payload.wardId = values.customer_ward_id;

  // Form giữ ngày sinh dạng DD/MM/YYYY (mask hiển thị); BE nhận ISO.
  const birthday = toIsoBirthday(values.customer_birthday);
  if (birthday) payload.birthday = birthday;

  const citizenId = trimmed(values.citizen_id);
  if (citizenId) {
    payload.idCard = citizenId;
    payload.idCardType = 'cccd';
  }
  const note = trimmed(values.description);
  if (note) payload.note = note;

  payload.type = userType;

  // Nhóm hoá đơn: MST/tên tổ chức chỉ có nghĩa với khách tổ chức, nhưng người
  // mua hàng (tên + CCCD trên hoá đơn) thì cả hai loại đều dùng.
  if (userType === 'organization') {
    const taxCode = trimmed(values.invoice_tax_id);
    if (taxCode) payload.taxCode = taxCode;
    const orgName = trimmed(values.invoice_name);
    if (orgName) payload.invoiceOrganizationName = orgName;
  }
  // TCR-2432 — hồ sơ khách nhận danh sách email hoá đơn, gửi BE dạng
  // "a@x.com,b@y.com" (bỏ khoảng trắng + trùng lặp).
  const taxEmail = trimmed(serializeEmailList(values.invoice_email));
  if (taxEmail) payload.taxEmail = taxEmail;
  const invoiceAddress = trimmed(values.invoice_address);
  if (invoiceAddress) payload.invoiceAddress = invoiceAddress;
  const buyerName = trimmed(values.buyer_name);
  if (buyerName) payload.invoiceBuyerName = buyerName;
  const buyerCccd = trimmed(values.buyer_cccd);
  if (buyerCccd) payload.invoiceBuyerCccd = buyerCccd;

  return payload;
}
